import { useQuery } from '@tanstack/react-query';
import {
  ArrowDown, ArrowLeft, ArrowRight, ArrowUp, CrosshairSimple, Intersect, X,
} from '@phosphor-icons/react';
import { apiLaminas } from '@/api/laminas';
import type { Hoja } from '@/api/laminas';

// F-6 de ARQ-007: comparar dos hojas. Medido en la segunda grabación de Procore
// (16-oct-2026): «comparación de planos contra OTRO plano (E-401 vs M-301) en
// rojo/azul con alternar hojas y alineación manual por D-pad».
//
// Eso trae una decisión de alcance que vale escribir: lo que se compara NO es
// solo otra VERSIÓN de la misma lámina. Son dos hojas cualesquiera — la
// arquitectónica contra la mecánica es el caso que más se usa en obra. Por eso
// el selector tiene dos grupos y no una lista de versiones.
//
// Cómo se ve la diferencia (sin procesar ni una imagen):
//   1. Cada hoja va dentro de un grupo aislado con un tinte encima en `screen`.
//      Sobre blanco, `screen` no hace nada; sobre la tinta negra del dibujo, da
//      el color. Resultado: el plano queda teñido y el papel sigue blanco.
//   2. La hoja B se compone sobre la A en `multiply`. Donde las dos tienen línea
//      el resultado es casi negro; donde solo está A queda rojo; donde solo está
//      B queda azul. Eso es exactamente la lectura que se busca: el rojo es lo
//      que se fue y el azul lo que llegó.
//
// El rojo y el azul NO son tokens de marca a propósito: son la convención
// medida de la comparación (y tienen que distinguirse entre sí, que es un
// requisito distinto de estar en paleta).

export const COLOR_A = '#E02020';
export const COLOR_B = '#2060E0';

export type ModoComparar = 'super' | 'alternar';

/** Dónde quedó la hoja B respecto de la A, en píxeles de la hoja A. */
export interface Desfase {
  x: number;
  y: number;
}

interface Props {
  hoja: Hoja;
  /** Las hojas de ESTA lámina: el grupo «versiones» del selector. */
  hojasVersion: Hoja[];
  idPlano?: number;
  idHojaB: number | null;
  /** Ya cargada. Sirve para avisar cuando no calza con la base. */
  hojaB?: Hoja | null;
  cargandoB?: boolean;
  modo: ModoComparar;
  cual: 'a' | 'b';
  desfase: Desfase;
  /** Cuánto mueve un toque del D-pad, en píxeles de hoja (depende del zoom). */
  pasoPx: number;
  onElegir: (idHoja: number | null) => void;
  onModo: (m: ModoComparar) => void;
  onCual: (c: 'a' | 'b') => void;
  onMover: (dx: number, dy: number) => void;
  onCentrar: () => void;
  onCerrar: () => void;
}

export function PanelComparar({
  hoja, hojasVersion, idPlano, idHojaB, hojaB, cargandoB,
  modo, cual, desfase, pasoPx,
  onElegir, onModo, onCual, onMover, onCentrar, onCerrar,
}: Props) {
  // El listado de láminas para el grupo «otras». Se pide acá y no en el visor
  // porque es lo único que lo necesita, y ya está en la caché: la grilla y el
  // navegador del juego usan la misma clave.
  const { data: laminas } = useQuery({
    queryKey: ['laminas'],
    queryFn: () => apiLaminas.lista(),
  });

  const versiones = hojasVersion.filter(h => h.id !== hoja.id);
  // Contra otra lámina se compara su hoja VIGENTE. Elegir también la versión de
  // otra lámina pediría cargar su detalle solo para llenar un combo; cuando
  // haga falta, el camino es abrir esa lámina y comparar desde ahí.
  const otras = (laminas || []).filter(p => p.id !== idPlano && p.hojaVigenteId !== null);

  const hayCandidatos = versiones.length > 0 || otras.length > 0;

  return (
    // `/95` y no `/97`: ver la nota en PanelCapas — `/97` no está en la escala
    // de Tailwind y no genera regla, así que el panel salía transparente.
    <div className="w-64 rounded-lg border border-brand-gray-200 bg-white/95 p-2 shadow-lg">
      <div className="mb-1.5 flex items-center gap-1.5 px-1">
        <Intersect size={14} weight="bold" className="text-brand-gray-500" />
        <p className="flex-1 text-xs font-semibold text-brand-gray-500">Comparar</p>
        <button type="button" onClick={onCerrar} aria-label="Cerrar la comparación"
          className="rounded p-0.5 text-brand-gray-400 hover:bg-brand-gray-100">
          <X size={13} weight="bold" />
        </button>
      </div>

      {!hayCandidatos ? (
        <p className="px-1 py-2 text-[11px] leading-snug text-brand-gray-400">
          No hay con qué comparar: esta lámina tiene una sola versión y no hay
          otras láminas archivadas.
        </p>
      ) : (
        <>
          <select
            data-comparar-select
            value={idHojaB ?? ''}
            onChange={e => onElegir(e.target.value ? Number(e.target.value) : null)}
            className="w-full rounded border border-brand-gray-300 px-2 py-1 text-xs text-brand-gray-500"
          >
            <option value="">Elegí contra qué comparar…</option>
            {versiones.length > 0 && (
              <optgroup label="Versiones de esta lámina">
                {versiones.map(h => (
                  <option key={h.id} value={h.id}>
                    v{h.version}{h.nombreArchivo ? ` · ${h.nombreArchivo}` : ''}
                  </option>
                ))}
              </optgroup>
            )}
            {otras.length > 0 && (
              <optgroup label="Otras láminas (versión vigente)">
                {otras.map(p => (
                  <option key={p.id} value={p.hojaVigenteId!}>{p.nombre}</option>
                ))}
              </optgroup>
            )}
          </select>

          {cargandoB && (
            <p className="mt-1.5 px-1 text-[11px] text-brand-gray-400">Cargando la hoja…</p>
          )}

          {idHojaB !== null && hojaB && (
            <>
              {/* Las dos advertencias honestas. Ninguna bloquea: comparar una
                  hoja girada distinto sigue sirviendo para leer el cajetín, y
                  decirlo es mejor que endrezarla a medias. */}
              {hojaB.rotacionGrados !== hoja.rotacionGrados && (
                <Nota>
                  Esta hoja está girada distinto ({hojaB.rotacionGrados}° contra{' '}
                  {hoja.rotacionGrados}°) y la comparación <strong>no la endereza</strong>.
                </Nota>
              )}
              {desencaja(hoja, hojaB) && (
                <Nota>
                  Los papeles no son del mismo tamaño ({hojaB.anchoPx}×{hojaB.altoPx} px
                  contra {hoja.anchoPx}×{hoja.altoPx}). La hoja comparada se escala
                  por el ancho; si las proporciones no calzan, el alto queda corrido.
                </Nota>
              )}

              <div className="my-2 h-px bg-brand-gray-200" />

              <Titulo>Cómo se ve</Titulo>
              <div className="flex gap-1">
                <Pastilla activo={modo === 'super'} datos={{ 'data-modo': 'super' }}
                  onClick={() => onModo('super')} titulo="Las dos encimadas, teñidas">
                  Superpuestas
                </Pastilla>
                <Pastilla activo={modo === 'alternar'} datos={{ 'data-modo': 'alternar' }}
                  onClick={() => onModo('alternar')} titulo="Una sola a la vez, para saltar entre las dos">
                  Alternar
                </Pastilla>
              </div>

              {modo === 'super' ? (
                // La leyenda es lo que vuelve legible la imagen: sin saber que
                // el rojo es la base, un plano bicolor no dice nada.
                <div className="mt-1.5 space-y-0.5 px-1">
                  <Leyenda color={COLOR_A} texto={`Base · v${hoja.version}`} />
                  <Leyenda color={COLOR_B} texto={`Comparada · v${hojaB.version}`} />
                </div>
              ) : (
                <div className="mt-1.5 flex gap-1">
                  <Pastilla activo={cual === 'a'} datos={{ 'data-cual': 'a' }}
                    onClick={() => onCual('a')} titulo="Ver la hoja base">
                    v{hoja.version} (base)
                  </Pastilla>
                  <Pastilla activo={cual === 'b'} datos={{ 'data-cual': 'b' }}
                    onClick={() => onCual('b')} titulo="Ver la hoja comparada">
                    v{hojaB.version}
                  </Pastilla>
                </div>
              )}

              <div className="my-2 h-px bg-brand-gray-200" />

              {/* El D-pad. Dos hojas que vienen del mismo ingestor calzan
                  píxel a píxel, pero un plano reescaneado o de otro cajetín
                  llega corrido unos milímetros y sin poder empujarlo la
                  comparación no se puede leer. El paso se mide en píxeles de
                  pantalla y se traduce al zoom actual: un paso fijo es
                  invisible de lejos e inmanejable de cerca. */}
              <Titulo>Alinear la comparada</Titulo>
              <div className="flex items-center gap-2">
                <div className="grid grid-cols-3 gap-0.5">
                  <span />
                  <Flecha datos="arriba" titulo="Subir la comparada" onClick={() => onMover(0, -pasoPx)}>
                    <ArrowUp size={12} weight="bold" />
                  </Flecha>
                  <span />
                  <Flecha datos="izquierda" titulo="Mover a la izquierda" onClick={() => onMover(-pasoPx, 0)}>
                    <ArrowLeft size={12} weight="bold" />
                  </Flecha>
                  <Flecha datos="centrar" titulo="Volver a cero" onClick={onCentrar}>
                    <CrosshairSimple size={12} weight="bold" />
                  </Flecha>
                  <Flecha datos="derecha" titulo="Mover a la derecha" onClick={() => onMover(pasoPx, 0)}>
                    <ArrowRight size={12} weight="bold" />
                  </Flecha>
                  <span />
                  <Flecha datos="abajo" titulo="Bajar la comparada" onClick={() => onMover(0, pasoPx)}>
                    <ArrowDown size={12} weight="bold" />
                  </Flecha>
                  <span />
                </div>
                <div className="min-w-0 flex-1">
                  <p data-desfase className="font-mono text-[11px] text-brand-gray-400">
                    {Math.round(desfase.x)}, {Math.round(desfase.y)} px
                  </p>
                  <p className="text-[10px] leading-snug text-brand-gray-400">
                    paso {Math.round(pasoPx)} px de hoja
                  </p>
                </div>
              </div>

              {/* La alineación NO se guarda: es de esta sesión, igual que el
                  zoom. Guardarla pediría una tabla por par de hojas y hoy no
                  hay caso que la pida — las dos hojas del mismo ingestor
                  calzan solas. */}
              <p className="mt-1.5 px-1 text-[10px] leading-snug text-brand-gray-400">
                La alineación no se guarda: vale para esta sesión.
              </p>
            </>
          )}
        </>
      )}
    </div>
  );
}

/** ¿Los dos papeles tienen proporciones distintas? Más de 1 % ya se nota. */
function desencaja(a: Hoja, b: Hoja) {
  if (a.anchoPx === b.anchoPx && a.altoPx === b.altoPx) return false;
  const propA = a.anchoPx / a.altoPx;
  const propB = b.anchoPx / b.altoPx;
  return Math.abs(propA - propB) / propA > 0.01 || a.anchoPx !== b.anchoPx;
}

function Titulo({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-0.5 px-1 text-[10px] font-bold uppercase tracking-wide text-brand-gray-400">
      {children}
    </p>
  );
}

function Nota({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-1.5 rounded border border-brand-yellow bg-brand-yellow/10 px-1.5 py-1 text-[10px] leading-snug text-brand-gray-500">
      {children}
    </p>
  );
}

function Leyenda({ color, texto }: { color: string; texto: string }) {
  return (
    <p className="flex items-center gap-1.5 text-[11px] text-brand-gray-500">
      <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: color }} />
      <span className="truncate">{texto}</span>
    </p>
  );
}

function Pastilla({ children, activo, onClick, titulo, datos }: {
  children: React.ReactNode;
  activo: boolean;
  onClick: () => void;
  titulo: string;
  datos: Record<string, string>;
}) {
  return (
    <button
      type="button"
      {...datos}
      aria-pressed={activo}
      title={titulo}
      onClick={onClick}
      className={`flex-1 truncate rounded px-2 py-1 text-[11px] font-semibold transition-colors ${
        activo ? 'bg-brand-gray-500 text-white' : 'text-brand-gray-500 hover:bg-brand-gray-100'
      }`}
    >
      {children}
    </button>
  );
}

function Flecha({ children, onClick, titulo, datos }: {
  children: React.ReactNode;
  onClick: () => void;
  titulo: string;
  datos: string;
}) {
  return (
    <button
      type="button"
      data-dpad={datos}
      title={titulo}
      onClick={onClick}
      className="flex h-6 w-6 items-center justify-center rounded text-brand-gray-500 hover:bg-brand-gray-100"
    >
      {children}
    </button>
  );
}
